import { useEffect } from "react";
import { keywordCategories } from "@/lib/seoKeywords";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  keywords?: string;
  keywordCategory?: keyof typeof keywordCategories;
  /** Platform-specific description for Twitter (shorter, punchier) */
  twitterDescription?: string;
  /** Platform-specific description for LinkedIn/OG (more professional) */
  ogDescription?: string;
  articleData?: {
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
  };
  faqData?: Array<{ question: string; answer: string }>;
}

// Master keyword set for comprehensive SEO coverage (30 high-intent keywords)
const masterKeywords = [
  // Utility Escape Keywords (High Intent)
  "escape PG&E rate hikes",
  "escape SCE rate hikes", 
  "SDG&E highest rates in nation",
  "California electricity rates 2026",
  "how to lower electric bill California",
  // NEM 3.0 Keywords
  "NEM 3.0 California explained",
  "NEM 3.0 battery storage required",
  "is solar worth it in California 2026",
  // Cost & Savings Keywords
  "solar panel cost California 2026",
  "$Zero down solar California",
  "how much do solar panels save California",
  "what is a solar PPA",
  // Battery Storage Keywords
  "best solar battery California 2026",
  "Tesla Powerwall 3 California",
  "home battery backup California",
  "blackout protection California",
  // Local SEO Keywords
  "best solar company Los Angeles 2026",
  "best solar company San Diego 2026",
  "best solar company Bay Area 2026",
  "solar installer near me California",
  // Comparison Keywords
  "Sunrun vs local solar company",
  "Tesla solar alternative California",
  "utility rates vs solar California",
  // General High-Intent Keywords
  "best solar company California 2026",
  "how to go solar California",
  "California solar incentives 2026",
  "residential solar California",
  "solar panels California",
  "energy independence California",
  "power purchase agreement solar"
].join(", ");

/**
 * SEO Head component for dynamic meta tag updates
 * Optimized for Google AI Overviews, SGE, and first-page rankings
 * Now includes 30+ high-intent keywords for California residential solar
 */
const SEOHead = ({
  title = "$0 Down Solar + Battery + Cash Rebate",
  description = "Escape rising utility rates with solar + battery storage. $Zero down PPA. Battery backup included. 25-year warranty.",
  canonicalUrl = "https://www.gosolar.pro/",
  ogImage = "https://www.gosolar.pro/og-image.jpg?v=10",
  ogType = "website",
  noIndex = false,
  keywords,
  keywordCategory,
  twitterDescription,
  ogDescription,
  articleData,
  faqData,
}: SEOHeadProps) => {
  // Build keywords from category if provided, otherwise use default master set
  const effectiveKeywords = keywords 
    ? keywords 
    : keywordCategory 
      ? keywordCategories[keywordCategory].join(", ") + ", " + masterKeywords
      : masterKeywords;
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (
      selector: string,
      attribute: string,
      value: string
    ) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        const [attr, attrValue] = selector
          .replace("meta[", "")
          .replace("]", "")
          .replace(/"/g, "")
          .split("=");
        element.setAttribute(attr, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    // Helper to add/update JSON-LD script
    const updateJsonLd = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Update description - conversational for AI
    updateMetaTag('meta[name="description"]', "content", description);
    
    // AI-friendly semantic meta tags
    updateMetaTag('meta[name="summary"]', "content", description);
    
    // Keywords meta tag (comprehensive 30+ high-intent keywords)
    updateMetaTag('meta[name="keywords"]', "content", effectiveKeywords);

    // Update robots with AI-friendly directives
    if (noIndex) {
      updateMetaTag('meta[name="robots"]', "content", "noindex, nofollow");
    } else {
      updateMetaTag(
        'meta[name="robots"]',
        "content",
        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      );
    }

    // Update Open Graph tags (use ogDescription if provided for LinkedIn/Facebook)
    updateMetaTag('meta[property="og:title"]', "content", title);
    updateMetaTag('meta[property="og:description"]', "content", ogDescription || description);
    updateMetaTag('meta[property="og:url"]', "content", canonicalUrl);
    updateMetaTag('meta[property="og:image"]', "content", ogImage);
    updateMetaTag('meta[property="og:type"]', "content", ogType);
    updateMetaTag('meta[property="og:site_name"]', "content", "California Energy Group");

    // Update Twitter tags (use twitterDescription for shorter, punchier copy)
    updateMetaTag('meta[name="twitter:card"]', "content", "summary_large_image");
    updateMetaTag('meta[name="twitter:title"]', "content", title);
    updateMetaTag('meta[name="twitter:description"]', "content", twitterDescription || description);
    updateMetaTag('meta[name="twitter:image"]', "content", ogImage);
    updateMetaTag('meta[name="twitter:site"]', "content", "@GoSolarPro");

    // Update (or create) canonical link
    let canonicalElement = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.rel = "canonical";
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.href = canonicalUrl;

    // Add Article structured data for blog posts
    if (articleData && ogType === "article") {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": articleData.author || "California Energy Group",
          "url": "https://www.gosolar.pro"
        },
        "publisher": {
          "@type": "Organization",
          "name": "California Energy Group",
          "logo": {
            "@type": "ImageObject",
            "url": "https://gosolar.pro/pwa-512x512.png"
          }
        },
        "datePublished": articleData.publishedTime || new Date().toISOString(),
        "dateModified": articleData.modifiedTime || new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl
        },
        "articleSection": articleData.section || "Solar Energy",
        "keywords": articleData.tags?.join(", ") || "solar panels, California, energy savings"
      };
      updateJsonLd("article-schema", articleSchema);

      // Add article meta tags
      updateMetaTag('meta[property="article:published_time"]', "content", articleData.publishedTime || "");
      updateMetaTag('meta[property="article:modified_time"]', "content", articleData.modifiedTime || "");
      updateMetaTag('meta[property="article:section"]', "content", articleData.section || "Solar Energy");
      updateMetaTag('meta[property="article:author"]', "content", articleData.author || "California Energy Group");
    }

    // Add FAQ structured data if provided
    if (faqData && faqData.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map((item) => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
      updateJsonLd("page-faq-schema", faqSchema);
    }

    // Cleanup function
    return () => {
      // Remove dynamic JSON-LD on unmount
      const articleScript = document.getElementById("article-schema");
      if (articleScript) articleScript.remove();
      const faqScript = document.getElementById("page-faq-schema");
      if (faqScript) faqScript.remove();
    };
  }, [title, description, canonicalUrl, ogImage, ogType, noIndex, effectiveKeywords, twitterDescription, ogDescription, articleData, faqData]);

  return null;
};

export default SEOHead;
